import React from "react";
import { useNavigate } from "react-router-dom";
import AnimatedSection from "../../../components/AnimatedSection";
import {
  MessageSquare,
  Search,
  Brain,
  Zap,
  TrendingUp,
  ArrowRight
} from "lucide-react";

export default function AnswerEngineOptimization() {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Search size={22} />,
      title: "Featured Snippet Optimization",
      desc: "Optimize content to rank in position zero and appear in instant answers.",
    },
    {
      icon: <MessageSquare size={22} />,
      title: "Conversational Query Targeting",
      desc: "Target question-based queries users ask on Google and AI platforms.",
    },
    {
      icon: <Brain size={22} />,
      title: "AI Answer Optimization",
      desc: "Structure content for visibility in ChatGPT, Google SGE, and AI assistants.",
    },
    {
      icon: <Zap size={22} />,
      title: "Zero-Click SEO Strategy",
      desc: "Capture traffic and visibility even when users don’t click search results.",
    },
    {
      icon: <TrendingUp size={22} />,
      title: "Authority & Entity Building",
      desc: "Strengthen brand authority to become a trusted answer source.",
    },
  ];

  const benefits = [
    "Appear in Google featured snippets",
    "Get visibility in ChatGPT & AI answers",
    "Capture zero-click search traffic",
    "Boost brand authority & trust",
    "Stay ahead in AI-driven search",
  ];

  const process = [
    "Search intent & query analysis",
    "Content restructuring for answers",
    "Snippet & AI optimization",
    "Schema & structured data implementation",
    "Performance tracking & scaling",
  ];

  return (
    <AnimatedSection>
      <div className="bg-gradient-to-b from-[#0f172a] via-[#0b1120] to-black text-white">

        {/* HERO */}
        <section className="py-32 text-center px-6 relative overflow-hidden">
          <div className="absolute w-[800px] h-[400px] bg-blue-600/20 blur-[150px] rounded-full top-0 left-1/2 -translate-x-1/2"></div>

          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Answer Engine
            <span className="text-blue-500"> Optimization (AEO)</span>
          </h1>

          <p className="text-gray-400 max-w-2xl mx-auto mt-6 text-lg">
            Optimize your content to appear in instant answers, AI responses,
            and featured snippets across Google and AI platforms.
          </p>

          <button
            onClick={() => navigate("/contact")}
            className="mt-10 px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-xl text-lg"
          >
            Get AEO Strategy →
          </button>
        </section>

        {/* SERVICES */}
        <section className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="text-3xl md:text-5xl font-bold text-center">
            Our AEO Services
          </h2>

          <div className="grid md:grid-cols-3 gap-10 mt-16">
            {services.map((item, i) => (
              <div
                key={i}
                className="bg-white/5 p-8 rounded-2xl border border-white/10 hover:border-blue-500 transition"
              >
                <div className="text-blue-500 mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-gray-400 mt-3">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* BENEFITS */}
        <section className="bg-white/5 py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-center">
              Why Answer Engine Optimization Matters
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mt-16">
              {benefits.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-black/40 p-6 rounded-xl border border-white/10"
                >
                  <span className="text-blue-500 text-xl">✓</span>
                  <p className="text-gray-300">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="py-24 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold">
              Our AEO Process
            </h2>

            <div className="mt-16 space-y-6">
              {process.map((step, i) => (
                <div
                  key={i}
                  className="flex items-center gap-6 bg-white/5 p-6 rounded-xl border border-white/10"
                >
                  <span className="text-blue-500 text-xl font-bold">
                    {i + 1}
                  </span>
                  <p className="text-gray-300">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10 text-center">
            <div className="bg-white/5 p-10 rounded-2xl border border-white/10">
              <h3 className="text-4xl font-bold text-blue-500">Top</h3>
              <p className="text-gray-400 mt-2">Position zero ranking</p>
            </div>

            <div className="bg-white/5 p-10 rounded-2xl border border-white/10">
              <h3 className="text-4xl font-bold text-blue-500">AI</h3>
              <p className="text-gray-400 mt-2">Answer visibility</p>
            </div>

            <div className="bg-white/5 p-10 rounded-2xl border border-white/10">
              <h3 className="text-4xl font-bold text-blue-500">0-Click</h3>
              <p className="text-gray-400 mt-2">Search dominance</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-28 px-6">
          <h2 className="text-3xl md:text-5xl font-bold">
            Ready to Dominate Answer-Based Search?
          </h2>

          <p className="text-gray-400 mt-6 max-w-xl mx-auto">
            Let’s position your business as the top answer across search engines and AI platforms.
          </p>

          <button
            onClick={() => navigate("/contact")}
            className="mt-10 px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-xl text-lg"
          >
            Start Now <ArrowRight className="inline ml-2" />
          </button>
        </section>

      </div>
    </AnimatedSection>
  );
}